/// FOURJS_START_COPYRIGHT(D,2014)
/// Property of Four Js*
/// (c) Copyright Four Js 2014, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("DebugAuiController", function (context, cls) {
  /**
   * @class DebugAuiController
   * @memberOf classes
   */
  class DebugAuiController {
    constructor() /** @lends classes.DebugAuiController.prototype */ {
      this.defaultTreeViewItemTemplate = {
        color: "#000000",
        collapsed: false
      };
      this.highlightAui = "highlightAui";

      this._nodeWidget = null;
      this._layoutWidget = null;
      /** @type classes.NodeBase */
      this._lastRootNode = null;
      this.auiSerial = null;
      this._currentSelectedNode = null;
      /** @type {classes.MonitorDebugTreeWidget} */
      this._treeWidget = cls.WidgetFactory.createWidget("MonitorDebugTree", {
        appHash: gbc.systemAppId
      });
    }

    /**
     * Destroy the controller properly
     */
    destroy() {
      if (this._layoutWidget) {
        this._layoutWidget.destroy();
      }
      if (this._nodeWidget) {
        this._nodeWidget.destroy();
      }
      this._treeWidget.destroy();
      this._treeWidget = null;
    }

    /**
     * Get the Debug widget
     * @return {classes.MonitorDebugTreeWidget|null}
     */
    getWidget() {
      return this._treeWidget;
    }

    /**
     * Create the node info widget
     * @param {number} id of the node
     * @return {*|HTMLElement}
     */
    _createNodeInfo(id) {
      this._nodeWidget = cls.WidgetFactory.createWidget("MonitorDebugNodeInfo", {
        appHash: gbc.systemAppId
      });
      this._displayProperties(this._nodeWidget.getPropertiesContainer(), id);
      return this._nodeWidget.getElement();
    }

    /**
     * Create the layout info widget
     * @param id
     * @return {*|HTMLElement}
     * @private
     */
    _createLayoutInfo(id) {
      this._layoutWidget = cls.WidgetFactory.createWidget("MonitorDebugLayoutInfo", {
        appHash: gbc.systemAppId
      });
      this._displayLayout(this._layoutWidget, id);
      return this._layoutWidget.getElement();
    }

    /**
     * Create the subtree item of a given node
     * @param node
     * @return {*|classes.WidgetBase}
     * @private
     */
    _createSub(node) {
      const widget = cls.WidgetFactory.createWidget("MonitorDebugTreeItem", {
        appHash: gbc.systemAppId
      });
      let label = node._tag;
      if (node.attribute("name")) {
        label += " (" + node.attribute("name") + ")";
      } else if (node.attribute("value")) {
        const value = node.attribute("value");
        label += " (" + (value.length > 16 ? value.substr(0, 16) + "\u2026" : value) + ")";
      }
      widget.setLabel(label);
      widget.setIdRef(node._id);
      const nodeInfo = context.constants.debugInfo.auiTreeNodeInfo[node._tag] || cls.DebugAuiController.defaultTreeViewItemTemplate;
      if (nodeInfo) {
        widget.setIconColor(nodeInfo.color);
        widget.setCollapsed(nodeInfo.collapsed);
      }
      widget.when(gbc.constants.widgetEvents.click, this.showNode.bind(this, node));
      return widget;
    }

    /**
     * Show the node in the tree and in the app
     * @param node
     * @param forceHighlight
     */
    showNode(node, forceHighlight) {
      forceHighlight = typeof forceHighlight !== "undefined" ? forceHighlight : false;
      this._currentSelectedNode = node;
      if (forceHighlight) {
        const event = new CustomEvent(cls.DebugAuiController.highlightAui, {
          bubbles: true,
          cancelable: false
        });
        event.auiNodeId = node._id;
        window.dispatchEvent(event);
      }
      this._treeWidget.setSelectedItem(node._id);
      this._treeWidget.setNodeDebugContent(this._createNodeInfo(node._id));
      this._treeWidget.setLayoutInfoContent(this._createLayoutInfo(node._id));
    }

    /**
     * Update the AUI debugger
     * @param {classes.NodeBase} node - node
     */
    refreshDebugAui(node) {
      const { gbcVersion, buildNumber, serverVersion, runtimeVersion } = this._appInfo;
      document.getElementsByClassName("version-number")[0].innerHTML = gbcVersion;
      document.getElementsByClassName("build-number")[0].innerHTML = buildNumber;
      document.getElementsByClassName("fgl-version")[0].innerHTML = runtimeVersion;
      document.getElementsByClassName("gas-version")[0].innerHTML = serverVersion;

      context.styler.bufferize();
      if (node?.getApplication()) {
        this.auiSerial = node.getApplication().getNode(0).auiSerial;
        this._lastRootNode = node;
        this._treeWidget.setFocusedWidget(node.attribute("focus"));

        const subs = function (w, children) {
          for (let i = 0; i < children.length; i++) {
            const widget = this._createSub(children[i]);
            w.addChildWidget(widget);
            subs(widget, children[i].getChildren());
          }
        }.bind(this);
        const rootWidget = this._createSub(node);
        subs(rootWidget, node.getChildren());

        cls.DOMElementHelper.empty(this._treeWidget);
        this._treeWidget.addChildWidget(rootWidget);
        context.styler.flush();
        if (this._currentSelectedNode) {
          this.showNode(this._currentSelectedNode, false);
        }
      } else {
        cls.DOMElementHelper.empty(this._treeWidget);
        if (this._nodeWidget) {
          this._nodeWidget.destroy();
        }
        if (this._layoutWidget) {
          this._layoutWidget.destroy();
        }
      }
    }

    /**
     * Display the layout information of the widget
     * @param {classes.MonitorDebugLayoutInfoWidget} widget - widget concerned
     * @param {number} refId - IdRef of the linked node
     */
    _displayLayout(widget, refId) {
      const app = this._lastRootNode.getApplication();
      const omNode = app.getNode(refId);
      if (omNode) {
        const w = omNode.getController() && omNode.getController().getWidget();
        const layoutInfo = w && w.getLayoutInformation();
        if (layoutInfo) {
          widget.setLayoutEngineName(w.getLayoutEngine() && w.getLayoutEngine().__name);
          widget.setPosX(layoutInfo ? layoutInfo.getGridX() : "???");
          widget.setPosY(layoutInfo ? layoutInfo.getGridY() : "???");
          widget.setGridWidth(layoutInfo ? layoutInfo.getGridWidth() : "???");
          widget.setGridHeight(layoutInfo ? layoutInfo.getGridHeight() : "???");
          widget.setWidth(layoutInfo ? layoutInfo.getPreferred().getWidth() : "???");
          widget.setHeight(layoutInfo ? layoutInfo.getPreferred().getHeight() : "???");
          widget.setMeasuredHasSize(layoutInfo ? layoutInfo.getMeasured().hasSize() : "???");
          widget.setMeasuredWidth(layoutInfo ? layoutInfo.getMeasured().getWidth() : "???");
          widget.setMeasuredHeight(layoutInfo ? layoutInfo.getMeasured().getHeight() : "???");
          widget.setMinimalHasSize(layoutInfo ? layoutInfo.getMinimal().hasSize() : "???");
          widget.setMinimalWidth(layoutInfo ? layoutInfo.getMinimal().getWidth() : "???");
          widget.setMinimalHeight(layoutInfo ? layoutInfo.getMinimal().getHeight() : "???");
          widget.setMaximalHasSize(layoutInfo ? layoutInfo.getMaximal().hasSize() : "???");
          widget.setMaximalWidth(layoutInfo ? layoutInfo.getMaximal().getWidth() : "???");
          widget.setMaximalHeight(layoutInfo ? layoutInfo.getMaximal().getHeight() : "???");
          widget.setAvailableHasSize(layoutInfo ? layoutInfo.getAvailable().hasSize() : "???");
          widget.setAvailableWidth(layoutInfo ? layoutInfo.getAvailable().getWidth() : "???");
          widget.setAvailableHeight(layoutInfo ? layoutInfo.getAvailable().getHeight() : "???");
          widget.setAllocatedHasSize(layoutInfo ? layoutInfo.getAllocated().hasSize() : "???");
          widget.setAllocatedWidth(layoutInfo ? layoutInfo.getAllocated().getWidth() : "???");
          widget.setAllocatedHeight(layoutInfo ? layoutInfo.getAllocated().getHeight() : "???");
          widget.setPreferredHasSize(layoutInfo ? layoutInfo.getPreferred().hasSize() : "???");
          widget.setPreferredWidth(layoutInfo ? layoutInfo.getPreferred().getWidth() : "???");
          widget.setPreferredHeight(layoutInfo ? layoutInfo.getPreferred().getHeight() : "???");
          widget.setDecoratingHasSize(layoutInfo ? layoutInfo.getDecorating().hasSize() : "???");
          widget.setDecoratingWidth(layoutInfo ? layoutInfo.getDecorating().getWidth() : "???");
          widget.setDecoratingHeight(layoutInfo ? layoutInfo.getDecorating().getHeight() : "???");
          widget.setDecoratingoffsetHasSize(layoutInfo ? layoutInfo.getDecoratingOffset().hasSize() : "???");
          widget.setDecoratingoffsetWidth(layoutInfo ? layoutInfo.getDecoratingOffset().getWidth() : "???");
          widget.setDecoratingoffsetHeight(layoutInfo ? layoutInfo.getDecoratingOffset().getHeight() : "???");
          widget.setStretchX(layoutInfo ? layoutInfo.getStretched().getX(true) : "???");
          widget.setStretchY(layoutInfo ? layoutInfo.getStretched().getY(true) : "???");
          widget.setChildrenStretchX(layoutInfo ? layoutInfo.isChildrenXStretched() : "???");
          widget.setChildrenStretchY(layoutInfo ? layoutInfo.isChildrenYStretched() : "???");
          widget.setInvalidatedMeasure(layoutInfo ? w._layoutEngine._invalidatedMeasure : "???");
          widget.setInvalidatedAllocatedSpace(layoutInfo ? w._layoutEngine._invalidatedAllocatedSpace : "???");
        } else {
          widget.setNoLayout();
        }
      }
    }

    /**
     * Display the properties of the node
     * @param propertyContainer
     * @param refId
     * @private
     */
    _displayProperties(propertyContainer, refId) {
      const app = this._lastRootNode.getApplication();
      const omNode = app && app.getNode(refId);
      if (omNode) {
        const values = {};
        const categories = {};
        context.constants.nodeAttributes[omNode._tag].forEach((property) => {
          values[property] = null;
          const cat = categories[context.constants.debugInfo.attributeCategory[property]] || [];
          cat.push(property);
          categories[context.constants.debugInfo.attributeCategory[property]] = cat.sort();
        });
        Object.keys(omNode._attributes).forEach((key) => {
          values[key] = omNode._attributes[key];
        });

        const cats = document.createDocumentFragment();

        Object.keys(categories)
          .sort((a, b) => a.localeCompare(b))
          .forEach((category) => {
            const cat = document.createElement("tr"),
              catText = document.createElement("td");
            catText.setAttribute("colspan", "5");
            catText.classList.add("category");
            catText.textContent = category;
            cat.appendChild(catText);
            cats.appendChild(cat);

            const pties = categories[category],
              len = pties.length;
            let i = 0;
            for (; i < len; i++) {
              const property = pties[i],
                defaultValue = cls.NodeHelper.getAttributeDefaultValue(omNode.tag, property);
              const pty = document.createElement("tr");
              let info = document.createElement("td");
              pty.classList.add("property", "cat_" + category);
              if (omNode._attributesSetByVM[property]) {
                pty.classList.add("changed");
              }
              info.textContent = "&nbsp;";
              pty.appendChild(info);
              info = document.createElement("td");
              info.textContent = property;
              pty.appendChild(info);
              info = document.createElement("td");
              info.textContent = values[property] === null ? defaultValue : values[property];
              pty.appendChild(info);
              info = document.createElement("td");
              info.textContent = property === "value" ? values[property] : ("" + values[property]).replaceAll(/\s/g, "_");
              pty.appendChild(info);
              info = document.createElement("td");
              info.textContent = defaultValue;
              pty.appendChild(info);

              cats.appendChild(pty);
            }
          });

        cls.DOMElementHelper.empty(propertyContainer);
        propertyContainer.appendChild(cats);
      }
    }
  }

  cls.DebugAuiController = DebugAuiController;
});
